'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import EnrollmentModal from '@/components/EnrollmentModal';
import EnrollmentForm from '@/components/EnrollmentForm';

export default function CTASection() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleEnrollClick = () => {
    if (!isHydrated) return;
    setIsModalOpen(true);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-primary/10 via-accent/10 to-sky-light/10 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-card rounded-3xl p-8 md:p-12 shadow-2xl border border-border">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-green-500/20 text-green-600 px-4 py-2 rounded-full mb-6 border-2 border-green-500/30">
              <Icon name="SparklesIcon" size={20} variant="solid" />
              <span className="text-sm font-bold uppercase tracking-wide">Limited Time Offer</span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-headline font-bold text-foreground mb-6">
              Start Your Professional Journey
              <span className="block mt-2 bg-gradient-to-r from-primary via-accent to-sky-light bg-clip-text text-transparent">
                Get FREE Internship Today!
              </span>
            </h2>

            <p className="text-lg text-muted-foreground mb-8">
              Join hundreds of students who are building real projects, mastering production codebases, 
              and launching their tech careers. Enroll in any course and get the complete internship experience at no extra cost.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={handleEnrollClick}
                className="px-10 py-5 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-xl font-headline font-bold text-lg shadow-sky-lg hover:shadow-sky-xl transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Enroll Now & Get FREE Internship</span>
                <Icon name="ArrowRightIcon" size={24} variant="solid" />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-green-500" />
                <span>No hidden charges</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-green-500" />
                <span>Flexible schedule</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-green-500" />
                <span>Certificate included</span>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="mt-12 pt-12 border-t border-border">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-headline font-bold text-primary mb-2">500+</div>
                <div className="text-sm text-muted-foreground">Successful Interns</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-headline font-bold text-primary mb-2">3</div>
                <div className="text-sm text-muted-foreground">Projects Built</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-headline font-bold text-primary mb-2">4.8★</div>
                <div className="text-sm text-muted-foreground">Average Rating</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-headline font-bold text-primary mb-2">90%</div>
                <div className="text-sm text-muted-foreground">Job Success Rate</div>
              </div>
            </div>
          </div>

          {/* Explore Courses */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              Want to see our courses first?
            </p>
            <Link
              href="/programs-overview"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 font-semibold transition-colors duration-300"
            >
              <span>Explore All Programs</span>
              <Icon name="ArrowRightIcon" size={18} variant="outline" />
            </Link>
          </div>
        </div>
      </div>

      {isHydrated && (
        <EnrollmentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <EnrollmentForm onClose={() => setIsModalOpen(false)} />
        </EnrollmentModal>
      )}
    </section>
  );
}
